import { useEffect, useState } from "react";
import { toast } from "sonner";
import { fetchReelsLatest } from "../lib/api";

/**
 * Landing page section that showcases the current month's Sora 2 video reel.
 * - Auto-plays muted + loops so mobile browsers permit it
 * - Renders nothing when no reel exists (silent hide)
 * - Download + Share buttons for redistribution
 */
export default function MonthlyReel() {
  const [reel, setReel] = useState(null);

  useEffect(() => {
    let cancelled = false;
    fetchReelsLatest()
      .then((data) => {
        if (!cancelled && data?.available) setReel(data);
      })
      .catch(() => { /* endpoint might not exist yet — silent */ });
    return () => { cancelled = true; };
  }, []);

  if (!reel) return null;

  const monthLabel = reel.month
    ? new Date(reel.month + "-01").toLocaleDateString("en-US", { month: "long", year: "numeric" })
    : "This month";

  const dateCoveredLabel = reel.date_covered
    ? new Date(reel.date_covered).toLocaleDateString("en-US", { year: "numeric", month: "long", day: "numeric" })
    : "";

  const backendBase = process.env.REACT_APP_BACKEND_URL || "";
  const reelUrl = `${backendBase}${reel.download_url}`;

  async function onShare() {
    const shareData = {
      title: `TimeCapsule 24 — ${monthLabel} reel`,
      text: `A 12-second nostalgic reel from ${dateCoveredLabel}. Watch on TimeCapsule 24.`,
      url: reelUrl,
    };
    if (navigator.share) {
      try { await navigator.share(shareData); } catch { /* user cancelled */ }
    } else {
      try {
        await navigator.clipboard.writeText(reelUrl);
        toast.success("Reel URL copied to clipboard");
      } catch {
        toast.error("Couldn't copy — long-press the video to save instead");
      }
    }
  }

  return (
    <section className="mt-16 border-t-2 border-ink pt-8" data-testid="monthly-reel-section">
      <div className="max-w-3xl mx-auto text-center">
        <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-press-red mb-3">
          🎬 The {monthLabel} reel
        </div>
        <h3 className="font-newspaper text-2xl sm:text-3xl text-ink font-bold mb-2">
          Twelve seconds of {dateCoveredLabel || "history"}
        </h3>
        <p className="text-sm text-ink-muted mb-6 max-w-md mx-auto">
          Rendered fresh with Sora 2 — a nostalgic postcard from the archives.
          Watch, download, or share to your feed.
        </p>

        <div className="relative border-2 border-ink bg-black overflow-hidden mx-auto max-w-2xl">
          <video
            src={reelUrl}
            controls
            playsInline
            autoPlay
            muted
            loop
            className="w-full h-auto block"
            data-testid="monthly-reel-video"
          >
            Your browser doesn't support HTML5 video.
          </video>
        </div>

        <div className="flex flex-col sm:flex-row gap-2 justify-center mt-5">
          <a
            href={reelUrl}
            download={`timecapsule24-${reel.month}.mp4`}
            className="inline-flex items-center justify-center gap-2 bg-paper-warm border border-ink font-mono uppercase tracking-widest text-xs px-6 py-3 text-ink hover:bg-ink hover:text-paper-warm transition-colors"
            data-testid="monthly-reel-download"
          >
            ↓ Download mp4
          </a>
          <button
            onClick={onShare}
            className="inline-flex items-center justify-center gap-2 bg-press-red border border-ink font-mono uppercase tracking-widest text-xs px-6 py-3 text-paper-warm hover:opacity-90"
            data-testid="monthly-reel-share"
          >
            ⇗ Share
          </button>
        </div>
        <p className="text-[10px] uppercase tracking-widest text-ink-muted mt-4">
          Post to TikTok · Reels · YouTube Shorts · X · Bluesky
        </p>
      </div>
    </section>
  );
}
